import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { OrderStatus } from '@teffy/shared-types'
import { api } from '../services/api'
import { buildKitchenTicket, buildReceiptTicket, printTicket, type PrintOrder } from '../services/print'
import { PageHeader, Card, Badge, Button, Modal } from '../components/ui'

interface OrderItem {
  id: string
  quantity: number
  price: number
  notes: string | null
  option: {
    name: string
    dish: { name: string }
  }
  guest?: { name: string } | null
}

interface Order {
  id: string
  status: OrderStatus
  total: number
  subtotal: number
  tipPercent?: number | null
  loyaltyDiscount?: number | null
  adminDiscountAmount?: number | null
  adminDiscountNote?: string | null
  orderNumber?: number | null
  customerName?: string | null
  createdAt: string
  serviceTiming: string
  table: { number: number; alias?: string | null; type: string }
  waiter: { name: string } | null
  items: OrderItem[]
}

const STATUS_COLOR: Record<string, 'yellow' | 'blue' | 'purple' | 'green' | 'gray' | 'red'> = {
  [OrderStatus.REQUESTED]:  'yellow',
  [OrderStatus.CONFIRMED]:  'blue',
  [OrderStatus.PREPARING]:  'purple',
  [OrderStatus.READY]:      'green',
  [OrderStatus.SERVED]:     'gray',
  [OrderStatus.PAID]:       'green',
  [OrderStatus.CANCELLED]:  'red',
}

const STATUS_LABEL: Record<string, string> = {
  [OrderStatus.REQUESTED]:  'Solicitada',
  [OrderStatus.CONFIRMED]:  'Confirmada',
  [OrderStatus.PREPARING]:  'Preparando',
  [OrderStatus.READY]:      'Lista',
  [OrderStatus.SERVED]:             'Servida',
  [OrderStatus.PAYMENT_REQUESTED]:  'Pago solicitado',
  [OrderStatus.PAID]:               'Pagada',
  [OrderStatus.CANCELLED]:          'Cancelada',
}

const ALL_STATUSES = [
  OrderStatus.REQUESTED,
  OrderStatus.CONFIRMED,
  OrderStatus.PREPARING,
  OrderStatus.READY,
  OrderStatus.SERVED,
  OrderStatus.PAYMENT_REQUESTED,
  OrderStatus.PAID,
  OrderStatus.CANCELLED,
]

function playNewOrderBeep() {
  try {
    const ctx = new AudioContext()
    ;[0, 0.22].forEach((delay) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.frequency.value = 880
      gain.gain.setValueAtTime(0, ctx.currentTime + delay)
      gain.gain.linearRampToValueAtTime(0.45, ctx.currentTime + delay + 0.04)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.2)
      osc.start(ctx.currentTime + delay)
      osc.stop(ctx.currentTime + delay + 0.2)
    })
    setTimeout(() => ctx.close(), 1000)
  } catch { /* contexto de audio bloqueado por el navegador antes de interacción */ }
}

export default function OrdersPage() {
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'ALL'>('ALL')
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [emailInput, setEmailInput] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const prevStatusMap = useRef<Map<string, OrderStatus>>(new Map())
  const [newOrderIds, setNewOrderIds] = useState<Set<string>>(new Set())

  const { data: restaurant } = useQuery<{ name: string; printingEnabled: boolean; address: string | null; razonSocial: string | null; rfc: string | null; regimenFiscal: string | null; direccionFiscal: string | null; urlFacturacion: string | null }>({
    queryKey: ['restaurant'],
    queryFn: async () => (await api.get('/restaurant')).data,
    staleTime: 60_000,
  })

  const { data: orders = [], isLoading } = useQuery<Order[]>({
    queryKey: ['orders'],
    queryFn: async () => (await api.get('/orders')).data,
    refetchInterval: 15_000,
  })

  const { mutate: sendEmail, isPending: isSendingEmail } = useMutation({
    mutationFn: async ({ orderId, email }: { orderId: string; email: string }) =>
      api.post(`/orders/${orderId}/send-receipt`, { email }),
    onSuccess: () => {
      setEmailSent(true)
      setEmailInput('')
      setTimeout(() => setEmailSent(false), 3000)
    },
  })

  const { mutate: cancelOrder, isPending: isCancelling } = useMutation({
    mutationFn: async (orderId: string) =>
      api.patch(`/orders/${orderId}/status`, { status: OrderStatus.CANCELLED }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] })
      setSelectedOrder(null)
    },
  })

  // Detectar órdenes nuevas y cambios de estado → sonido + highlight + impresión
  useEffect(() => {
    if (orders.length === 0) return
    const restaurantName = restaurant?.name ?? 'Restaurante'
    const isInitialLoad = prevStatusMap.current.size === 0
    const freshIds: string[] = []

    orders.forEach((order) => {
      const prev = prevStatusMap.current.get(order.id)
      const curr = order.status

      if (!prev && !isInitialLoad) {
        // Orden completamente nueva (llegó entre polls)
        freshIds.push(order.id)
      }

      if (prev && prev !== curr && restaurant?.printingEnabled) {
        const po = orderToPrintOrder(order, restaurantName)
        if (curr === OrderStatus.CONFIRMED) printTicket(buildKitchenTicket(po))
        if (curr === OrderStatus.PAID) printTicket(buildReceiptTicket(po))
      }

      prevStatusMap.current.set(order.id, curr)
    })

    if (freshIds.length > 0) {
      playNewOrderBeep()
      setNewOrderIds((prev) => {
        const next = new Set(prev)
        freshIds.forEach((id) => next.add(id))
        return next
      })
      const timer = setTimeout(() => {
        setNewOrderIds((prev) => {
          const next = new Set(prev)
          freshIds.forEach((id) => next.delete(id))
          return next
        })
      }, 6000)
      return () => clearTimeout(timer)
    }
  }, [orders, restaurant])

  function orderToPrintOrder(order: Order, restaurantName: string): PrintOrder {
    return {
      id: order.id,
      orderNumber: order.orderNumber,
      customerName: order.customerName,
      tableNumber: order.table.number,
      tableType: (order.table.type ?? 'DINE_IN') as 'DINE_IN' | 'TAKEOUT',
      serviceTiming: order.serviceTiming,
      status: order.status,
      createdAt: order.createdAt,
      items: order.items.map((i) => ({
        quantity: i.quantity,
        dishName: i.option.dish.name,
        optionName: i.option.name,
        price: Number(i.price),
        notes: i.notes,
        guestName: i.guest?.name,
      })),
      subtotal: Number(order.subtotal),
      total: Number(order.total),
      tipPercent: order.tipPercent ?? 0,
      loyaltyDiscount: Number(order.loyaltyDiscount ?? 0),
      restaurantName,
      restaurantAddress: restaurant?.address,
      razonSocial:       restaurant?.razonSocial,
      rfc:               restaurant?.rfc,
      regimenFiscal:     restaurant?.regimenFiscal,
      direccionFiscal:   restaurant?.direccionFiscal,
      urlFacturacion:    restaurant?.urlFacturacion,
    }
  }

  const filtered = statusFilter === 'ALL'
    ? orders
    : orders.filter((o) => o.status === statusFilter)

  const totalRevenue = orders
    .filter((o) => o.status === OrderStatus.PAID)
    .reduce((acc, o) => acc + Number(o.total), 0)

  return (
    <div className="p-8">
      <PageHeader
        title="Órdenes"
        subtitle={`${orders.length} orden(es) en total · $${totalRevenue.toFixed(2)} MXN cobrados`}
      />

      {/* Filtros por estado */}
      <div className="flex flex-wrap gap-2 mb-6">
        <FilterChip
          active={statusFilter === 'ALL'}
          onClick={() => setStatusFilter('ALL')}
          label={`Todas (${orders.length})`}
        />
        {ALL_STATUSES.map((s) => {
          const count = orders.filter((o) => o.status === s).length
          if (count === 0) return null
          return (
            <FilterChip
              key={s}
              active={statusFilter === s}
              onClick={() => setStatusFilter(s)}
              label={`${STATUS_LABEL[s]} (${count})`}
            />
          )
        })}
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-400">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-gray-400 text-sm">Sin órdenes para este filtro</div>
      ) : (
        <Card>
          <div className="divide-y divide-gray-50">
            {filtered.map((o) => {
              const isNew = newOrderIds.has(o.id)
              return (
              <div
                key={o.id}
                className={`px-5 py-3 flex items-center justify-between cursor-pointer transition-colors duration-500 ${
                  isNew ? 'bg-amber-50 border-l-4 border-amber-400' : 'hover:bg-gray-50 border-l-4 border-transparent'
                }`}
                onClick={() => { setSelectedOrder(o); setEmailInput(''); setEmailSent(false) }}
              >
                <div className="flex items-center gap-4">
                  <div>
                    <span className="font-semibold text-gray-900">{o.table.alias ?? `Mesa ${o.table.number}`}</span>
                    {isNew && <span className="ml-2 text-[10px] font-bold bg-amber-400 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wide">Nueva</span>}
                    <span className="text-xs text-gray-400 ml-2">
                      {new Date(o.createdAt).toLocaleString('es-MX', {
                        month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <Badge
                    label={STATUS_LABEL[o.status] ?? o.status}
                    color={STATUS_COLOR[o.status] ?? 'gray'}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">{o.items.length} platillo(s)</span>
                  <span className="font-bold text-gray-900">${Number(o.total).toFixed(2)}</span>
                  <button
                    title="Imprimir ticket de cocina"
                    onClick={(e) => { e.stopPropagation(); printTicket(buildKitchenTicket(orderToPrintOrder(o, restaurant?.name ?? 'Restaurante'))) }}
                    className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
                    </svg>
                  </button>
                  <button
                    title="Imprimir recibo"
                    onClick={(e) => { e.stopPropagation(); printTicket(buildReceiptTicket(orderToPrintOrder(o, restaurant?.name ?? 'Restaurante'))) }}
                    className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                    </svg>
                  </button>
                  <span className="text-xs text-gray-400">Ver</span>
                </div>
              </div>
              )
            })}
          </div>
        </Card>
      )}

      {/* Modal de detalle */}
      <Modal
        title={selectedOrder ? `Orden — Mesa ${selectedOrder.table.number}` : ''}
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
      >
        {selectedOrder && (
          <>
            <OrderDetail
              order={selectedOrder}
              onUpdated={(updated) => {
                setSelectedOrder(updated)
                qc.invalidateQueries({ queryKey: ['orders'] })
              }}
            />
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => printTicket(buildKitchenTicket(orderToPrintOrder(selectedOrder, restaurant?.name ?? 'Restaurante')))}
                >
                  Imprimir cocina
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => printTicket(buildReceiptTicket(orderToPrintOrder(selectedOrder, restaurant?.name ?? 'Restaurante')))}
                >
                  Imprimir recibo
                </Button>
                {selectedOrder.status !== OrderStatus.CANCELLED && selectedOrder.status !== OrderStatus.PAID && (
                  <Button
                    variant="danger"
                    loading={isCancelling}
                    onClick={() => {
                      if (confirm('¿Cancelar esta orden? Esta acción no se puede deshacer.')) {
                        cancelOrder(selectedOrder.id)
                      }
                    }}
                  >
                    Cancelar orden
                  </Button>
                )}
              </div>
              <div className="flex gap-2 items-center">
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
                <Button
                  variant="secondary"
                  disabled={!emailInput || isSendingEmail}
                  loading={isSendingEmail}
                  onClick={() => sendEmail({ orderId: selectedOrder.id, email: emailInput })}
                >
                  {emailSent ? '✓ Enviado' : 'Enviar por correo'}
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}

function FilterChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors
        ${active
          ? 'bg-gray-900 text-white'
          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
        }`}
    >
      {label}
    </button>
  )
}

const TIMING_LABEL: Record<string, string> = {
  AS_THEY_COME: 'Conforme vayan saliendo',
  ALL_TOGETHER: 'Todo junto',
  AT_THE_END: 'Al final',
}

function OrderDetail({ order, onUpdated }: { order: Order; onUpdated: (updated: Order) => void }) {
  const [discountAmount, setDiscountAmount] = useState(
    Number(order.adminDiscountAmount ?? 0) > 0 ? String(Number(order.adminDiscountAmount)) : ''
  )
  const [discountNote, setDiscountNote] = useState(order.adminDiscountNote ?? '')
  const [discountError, setDiscountError] = useState('')

  const canApplyDiscount = order.status === OrderStatus.PAYMENT_REQUESTED

  const { mutate: applyDiscount, isPending: isApplying } = useMutation({
    mutationFn: async () => {
      const amount = Number(discountAmount)
      if (isNaN(amount) || amount < 0) throw new Error('Monto inválido')
      const { data } = await api.patch(`/orders/${order.id}/admin-discount`, {
        amount,
        note: discountNote.trim() || undefined,
      })
      return data as Order
    },
    onSuccess: (updated) => {
      setDiscountError('')
      onUpdated(updated)
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (e as Error)?.message
        ?? 'Error al aplicar descuento'
      setDiscountError(msg)
    },
  })

  const appliedDiscount = Number(order.adminDiscountAmount ?? 0)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">Estado</span>
        <Badge
          label={STATUS_LABEL[order.status] ?? order.status}
          color={STATUS_COLOR[order.status] ?? 'gray'}
        />
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">Fecha</span>
        <span className="text-gray-900">
          {new Date(order.createdAt).toLocaleString('es-MX', {
            dateStyle: 'medium', timeStyle: 'short',
          })}
        </span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">Servicio</span>
        <span className="text-gray-900">{TIMING_LABEL[order.serviceTiming] ?? order.serviceTiming}</span>
      </div>
      {order.waiter && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Mesero</span>
          <span className="text-gray-900">{order.waiter.name}</span>
        </div>
      )}

      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Platillos</p>
        <div className="space-y-2">
          {order.items.map((item) => (
            <div key={item.id} className="flex items-start justify-between text-sm">
              <div>
                <span className="text-gray-900">
                  {item.quantity}× {item.option.dish.name}
                  {item.option.name !== 'Estándar' && item.option.name !== 'default' && (
                    <span className="text-gray-400 ml-1">({item.option.name})</span>
                  )}
                </span>
                {item.guest && (
                  <p className="text-xs text-gray-400 mt-0.5">Para: {item.guest.name}</p>
                )}
                {item.notes && (
                  <p className="text-xs text-gray-400 mt-0.5">📝 {item.notes}</p>
                )}
              </div>
              <span className="text-gray-700 font-medium">
                ${(item.quantity * Number(item.price)).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Totales */}
      <div className="border-t border-gray-100 pt-3 space-y-1.5">
        <div className="flex justify-between text-sm text-gray-500">
          <span>Subtotal</span>
          <span>${Number(order.subtotal).toFixed(2)}</span>
        </div>
        {appliedDiscount > 0 && (
          <div className="flex justify-between text-sm text-orange-600">
            <span>Descuento discrecional{order.adminDiscountNote ? ` — ${order.adminDiscountNote}` : ''}</span>
            <span>−${appliedDiscount.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between font-black text-lg text-gray-900 pt-1">
          <span>Total</span>
          <span>${Number(order.total).toFixed(2)}</span>
        </div>
      </div>

      {/* Panel de descuento discrecional — solo en PAYMENT_REQUESTED */}
      {canApplyDiscount && (
        <div className="border-t border-dashed border-orange-200 pt-4">
          <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-3">
            Descuento discrecional
          </p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Monto ($)</label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={discountAmount}
                onChange={(e) => setDiscountAmount(e.target.value)}
                placeholder="0.00"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Motivo (opcional)</label>
              <input
                type="text"
                value={discountNote}
                onChange={(e) => setDiscountNote(e.target.value)}
                placeholder="Ej: Demora en servicio"
                maxLength={200}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
          </div>
          {Number(discountAmount) > 0 && (
            <p className="text-xs text-gray-400 mb-2">
              Nuevo total: <span className="font-semibold text-gray-700">
                ${Math.max(0, Number(order.total) - Number(discountAmount) + appliedDiscount).toFixed(2)}
              </span>
            </p>
          )}
          {discountError && <p className="text-xs text-red-500 mb-2">{discountError}</p>}
          <Button
            loading={isApplying}
            disabled={!discountAmount || Number(discountAmount) < 0}
            onClick={() => applyDiscount()}
          >
            Aplicar descuento
          </Button>
        </div>
      )}
    </div>
  )
}
